import { Flex, ActionIcon, useMantineTheme } from "@mantine/core";
import { useMemo, useState } from "react";
import {
  AGGridDefault,
  Modal,
  Pagination,
  TagCapsules,
} from "../../../components";
import { IQueryPredictiveOrder } from "../../../interfaces/IQuery";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { convertToDateAndHour, formatUtcDateOnly } from "../../../utils/date";
import { useTranslation } from "react-i18next";
import HeadingOperation from "../components/HeadingOperation";
import { ColDef } from "ag-grid-community";
import dataProcessing from "../../../utils/dataProcessing";
import { IColor } from "../../../utils/selectColors";
import ModalReject from "../components/ModalReject";
import {
  ECategoryPredictive,
  IListPredictiveOrder,
  cancelPredictiveOrder,
  usePredictiveOrder,
} from "../../../hooks/usePredictives";
import { formatCurrency } from "../../../utils/currency";
import { client_selected } from "../../../client";
import { Edit, Receipt1, Trash } from "iconsax-react";
import {
  colorTextPredictiveOrderPercentage,
  colorTextPredictiveOrderSide,
  colorTextPredictiveOrderStatus,
  isBloquedEditOrDelete,
  orderTypeText,
  validityText,
} from "../../../utils/PredictivesFunctions";
import ModalDetails from "./ModalDetails";
import ModalEdit from "./ModalEdit";

export default function OperationPredictives() {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const queryClient = useQueryClient();
  const [isLoadingModal, setIsLoadingModal] = useState(false);
  const [isSuccessDeleteModal, setIsSuccessDeleteModal] = useState(false);

  const [openModalError, setOpenModalError] = useState(
    {} as IListPredictiveOrder
  );
  const [openModalAction, setOpenModalAction] = useState<{
    action: "Edit" | "Remove" | "View" | "";
    id: number | null;
  }>({ action: "", id: null });

  const defaultQuery: IQueryPredictiveOrder = {
    page: 1,
    pageSize: 10,
    sortOrder: "DESC",
    orderBy: "sending_time",
  };

  const [query, setQuery] = useState<IQueryPredictiveOrder>({
    ...defaultQuery,
  });

  const {
    data: dataOrderPosition,
    isLoading,
    isFetching,
  } = usePredictiveOrder(query);
  // if (isError && isFetched && !data) return <PageNotFound />;

  const handleUpdate = () => {
    queryClient.refetchQueries({
      queryKey: [
        "PredictiveOrder",
        query.page,
        query.pageSize,
        query.orderBy,
        query.sortOrder,
      ],
    });
  };

  const pagination = {
    page: query.page || 1,
    pageSize: query.pageSize || 10,
    totalElements: dataOrderPosition?.totalElements,
    totalPages: dataOrderPosition?.totalPages,
    view: dataOrderPosition?.data?.length || 0,
  };

  const handleAction = (
    action: "Edit" | "Remove" | "View" | "",
    id: number | null
  ) => {
    setOpenModalAction({ action, id });
    console.log("Action", action, id);
  };

  const handleRowClick = (row: any) => {
    const hasError =
      row?.order_status?.includes("REJECTED") && !!row?.reject_desc;
    if (hasError) setOpenModalError(row);
  };

  const handleCloseModalError = () => {
    setOpenModalError({} as IListPredictiveOrder);
  };

  const handleCloseModalAction = () => {
    setIsSuccessDeleteModal(false);
    setOpenModalAction({ action: "", id: null });
  };

  // Encontra os dados da ordem selecionada para edição
  const selectedOrderData =
    openModalAction.action === "Edit" && openModalAction.id
      ? dataOrderPosition?.data?.find(
          (order) => order.id === openModalAction.id
        ) || null
      : null;

  const { mutate: cancelOrderPredictive } = useMutation({
    mutationFn: async (id: number) => {
      const response = await cancelPredictiveOrder(id);
      return response;
    },
    onSuccess: () => {
      setIsSuccessDeleteModal(true);
    },
    onError: () => {
      handleCloseModalAction();
    },
  });

  const handleDeleteOrderPredictive = async () => {
    setIsLoadingModal(true);
    if (!openModalAction.id) return;
    await cancelOrderPredictive(openModalAction.id);
    setIsLoadingModal(false);
  };

  const columnDefs = useMemo<ColDef<any>[]>(
    () => [
      {
        headerName: t("operacoes.preditivos.table.category"),
        field: "category",
        suppressColumnsToolPanel: true,
        width: 120,
        cellRenderer: (params: any) => {
          return params.data?.category ? (
            <TagCapsules
              mr={12}
              border
              colorTag={
                params.data?.category === ECategoryPredictive.copom
                  ? IColor.copom
                  : IColor.green
              }
            >
              {params.data?.category}
            </TagCapsules>
          ) : (
            "---"
          );
        },
      },
      {
        headerName: t("operacoes.preditivos.table.contract"),
        field: "percentage",
        suppressColumnsToolPanel: true,
        width: 120,
        cellRenderer: (params: any) => {
          const percentageColorText = colorTextPredictiveOrderPercentage(
            params.data?.percentage
          );
          return (
            <TagCapsules border colorTag={percentageColorText.color}>
              {`${t(percentageColorText.text)} ${
                !!percentageColorText.percentage
                  ? ` ${percentageColorText.percentage}%`
                  : ""
              }`}
            </TagCapsules>
          );
        },
      },
      {
        headerName: t("operacoes.preditivos.table.response"),
        field: "order_side",
        suppressColumnsToolPanel: true,
        width: 120,
        cellRenderer: (params: any) => {
          const orderSide = colorTextPredictiveOrderSide(
            params.data?.order_side
          );
          return params.data?.order_side ? (
            <TagCapsules border colorTag={orderSide.color}>
              {t(orderSide.text)}
            </TagCapsules>
          ) : (
            "---"
          );
        },
      },
      {
        headerName: t("operacoes.preditivos.table.expiration"),
        field: "expiration_product",
        suppressColumnsToolPanel: true,
        width: 200,
        cellRenderer: (params: any) => {
          return `${formatUtcDateOnly(params.data?.expiration_product)}`;
        },
      },
      {
        headerName: t("operacoes.preditivos.table.volume"),
        field: "quantity",
        suppressColumnsToolPanel: true,
        width: 90,
        cellRenderer: (params: any) => dataProcessing(params.data?.quantity),
      },
      {
        headerName: t("operacoes.preditivos.table.probability"),
        field: "probability",
        suppressColumnsToolPanel: true,
        width: 80,
        cellRenderer: (params: any) =>
          params.data?.probability ? `${params.data?.probability}%` : "---",
      },
      {
        headerName: t("operacoes.preditivos.table.total"),
        field: "total",
        suppressColumnsToolPanel: true,
        width: 100,
        cellRenderer: (params: any) => formatCurrency(params.data?.total),
      },
      {
        headerName: t("operacoes.preditivos.table.status"),
        field: "order_status",
        suppressColumnsToolPanel: true,
        width: 200,
        cellRenderer: (params: any) => {
          return params.data?.order_status ? (
            <TagCapsules
              mr={12}
              border
              colorTag={
                colorTextPredictiveOrderStatus(params.data?.order_status).color
              }
            >
              {t(
                colorTextPredictiveOrderStatus(params.data?.order_status).text
              )}
            </TagCapsules>
          ) : (
            "---"
          );
        },
      },
      {
        headerName: t("operacoes.preditivos.table.data_execution"),
        field: "sending_time",
        suppressColumnsToolPanel: true,
        width: 170,
        cellRenderer: (params: any) =>
          convertToDateAndHour(params.data?.sending_time),
      },
      {
        headerName: t("operacoes.preditivos.table.decision"),
        field: "decision",
        suppressColumnsToolPanel: true,
        width: 120,
        cellRenderer: (params: any) => {
          const percentageColorText = colorTextPredictiveOrderPercentage(
            params.data?.decision
          );
          return params.data?.decision === null ||
            params.data?.decision === undefined ? (
            "----"
          ) : (
            <TagCapsules border colorTag={percentageColorText.color}>
              {`${t(percentageColorText.text)} ${
                !!percentageColorText.percentage
                  ? ` ${percentageColorText.percentage}%`
                  : ""
              }`}
            </TagCapsules>
          );
        },
      },
      {
        headerName: t("operacoes.preditivos.table.validity"),
        field: "validity",
        suppressColumnsToolPanel: true,
        width: 120,
        cellRenderer: (params: any) => t(validityText(params.data?.validity)),
      },
      {
        headerName: t("operacoes.preditivos.table.order_type"),
        field: "order_type",
        suppressColumnsToolPanel: true,
        width: 180,
        cellRenderer: (params: any) =>
          t(orderTypeText(params.data?.order_type)),
      },
      // {
      //   headerName: t("operacoes.preditivos.table.Opções"),
      //   field: "no_filter_details",
      //   suppressColumnsToolPanel: true,
      //   pinned: "right",
      //   editable: false,
      //   suppressMovable: true,
      //   width: 110,
      //   cellStyle: {
      //     cursor: "unset",
      //   },
      //   cellRenderer: (params: any) => {
      //     const idDetail = params?.data?.id;
      //     return (
      //       <Flex gap="8px" align="center">
      //         <ActionIcon
      //           variant="transparent"
      //           onClick={() => handleAction("Edit", idDetail)}
      //           mih={"24px"}
      //           miw={"24px"}
      //           w={"24px"}
      //           h={"24px"}
      //           disabled={isBloquedEditOrDelete(params.data?.order_status)}
      //         >
      //           <Edit
      //             size="24"
      //             color={
      //               isBloquedEditOrDelete(params.data?.order_status)
      //                 ? theme.colors.basic[4]
      //                 : theme.colors.primary[1]
      //             }
      //           />
      //         </ActionIcon>
      //         <ActionIcon
      //           variant="transparent"
      //           onClick={() => handleAction("Remove", idDetail)}
      //           mih={"24px"}
      //           miw={"24px"}
      //           w={"24px"}
      //           h={"24px"}
      //           disabled={isBloquedEditOrDelete(params.data?.order_status)}
      //         >
      //           <Trash
      //             size="24"
      //             color={
      //               isBloquedEditOrDelete(params.data?.order_status)
      //                 ? theme.colors.basic[4]
      //                 : theme.colors.primary[1]
      //             }
      //           />
      //         </ActionIcon>
      //         <ActionIcon
      //           variant="transparent"
      //           onClick={() => handleAction("View", idDetail)}
      //           mih={"24px"}
      //           miw={"24px"}
      //           w={"24px"}
      //           h={"24px"}
      //         >
      //           <Receipt1 size="24" color={client_selected.colors.primary[1]} />
      //         </ActionIcon>
      //       </Flex>
      //     );
      //   },
      // },
    ],
    [t]
  );

  return (
    <Flex
      justify="space-between"
      align="center"
      bg="basic.0"
      p={{ base: "12px", 991: 32 }}
      gap="24px"
      wrap={{ base: "wrap" }}
      sx={{ borderRadius: "12px 12px 0 0" }}
      direction={"column"}
      mb="120px"
    >
      <Flex
        direction={"column"}
        gap={24}
        justify="space-between"
        align="start"
        w="100%"
      >
        <HeadingOperation
          lastUpdated={dataOrderPosition?.lastUpdated}
          handleUpdate={handleUpdate}
          title={t("operacoes.preditivos.title")}
        />
      </Flex>
      <AGGridDefault
        title={t("operacoes.preditivos.subtitle")}
        rowData={dataOrderPosition?.data || []}
        columnDefs={columnDefs}
        isLoading={isLoading || isFetching}
        pagination={
          <Pagination
            pagination={pagination}
            onChange={(page) => setQuery({ ...query, page: page })}
          />
        }
        onRowClick={handleRowClick}
        storageKey={`columns-predictives-orders`}
        context={{ query, setQuery, hasFilters: false }}
      />
      <ModalReject
        handleCloseModalError={handleCloseModalError}
        clorderid={openModalError.id}
        help_reject_url={openModalError.help_reject_url}
        reject_desc={openModalError.reject_desc}
      />
      <Modal
        open={openModalAction.action === "Remove"}
        description={t("operacoes.preditivos.modal_remove.description")}
        type="warning"
        firstClick={handleDeleteOrderPredictive}
        isLoading={isLoadingModal}
        setClose={handleCloseModalAction}
        secondClick={handleCloseModalAction}
        titleFirstButton={t("global.button.yes")}
        titleSeccondButton={t("global.button.no_cancel")}
      />
      <ModalDetails
        id={openModalAction.action === "View" ? openModalAction.id : null}
        setClose={handleCloseModalAction}
      />
      <Modal
        type={"success"}
        titleSeccondButton={t("global.modal.close")}
        description={t("operacoes.preditivos.modal_success.description")}
        open={isSuccessDeleteModal}
        setClose={handleCloseModalAction}
        secondClick={handleCloseModalAction}
      />
      <ModalEdit
        setClose={handleCloseModalAction}
        id={openModalAction.action === "Edit" ? openModalAction.id : null}
      />
    </Flex>
  );
}
